/**
 * CreateModelModal Component
 * 
 * Modal dialog for creating a new MMS model by uploading an INP file.
 */

import React, { useState, useContext } from 'react';
import { Modal, Button, Input, Textarea } from '@hydrodigital/design-system';
import { Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import HydraulicSimulatorService from '@/domain/HydraulicSimulator/HydraulicSimulatorService';
import DashboardContext from '@/contexts/DashboardContext';

const hydraulicSimulatorService = new HydraulicSimulatorService();

export interface CreateModelModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: (modelId: string, fileName: string) => void;
}

export const CreateModelModal: React.FC<CreateModelModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
}) => {
	const { value: context } = useContext(DashboardContext);
	const [alias, setAlias] = useState('');
	const [description, setDescription] = useState('');
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	

	const handleClose = () => {
		if (!isCreating) {
			setAlias('');
			setDescription('');
			setUploadedFile(null);
			onClose();
		}
	};

	const blobToBase64 = async (blob: Blob): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onload = () => {
				if (reader.result) {
					const result = reader.result;
					resolve((result as string).split(',')[1]);
				} else {
					resolve('');
				}
			};
			reader.onerror = (error) => reject(error);
		});
	};

	const handleCreate = async () => {
		if (!uploadedFile || !alias.trim()) {
			notification.warning({
				message: 'Missing Information',
				description: 'Please provide a model name and upload an INP file.',
			});
			return;
		}

		if (!context.selectedLocation) {
			notification.error({
				message: 'No Location Selected',
				description: 'Please select a location before creating a model.',
			});
			return;
		}

		setIsCreating(true);

		try {
			const security = localStorage.getItem('security');
			const organizationId = JSON.parse(security || '{}').organizationId;

			const fileBase64 = await blobToBase64(uploadedFile);

			const response = await hydraulicSimulatorService.postInpAssets(
				fileBase64,
				uploadedFile.name,
				{
					fileName: uploadedFile.name,
					file: fileBase64,
					locationId: context.selectedLocation,
					city_center_coordinates: '54.978, -1.6102', // TODO: Get from location
					organizationId,
					alias: alias.trim(),
					description: description.trim(),
				}
			);

			if (response?.model_id) {
				notification.success({
					message: 'Model Created',
					description: `Model "${alias}" has been created successfully.`,
				});

				onSuccess(response.model_id, alias);
				handleClose();
			} else {
				throw new Error('No model ID returned from server');
			}
		} catch (error) {
			console.error('Error creating model:', error);
			notification.error({
				message: 'Creation Failed',
				description: 'Failed to create the model. Please try again.',
			});
		} finally {
			setIsCreating(false);
		}
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		accept: '.inp',
		showUploadList: false,
		beforeUpload: (file: File) => {
			setUploadedFile(file);
			return false; // Prevent auto upload
		},
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose}>
			<Modal.Header>
				<Modal.Title text="Create New Model" />
			</Modal.Header>

			<Modal.Content>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Select a Model
						</label>
						<Input
							placeholder=""
							value={uploadedFile?.name ?? ''}
							readOnly
							size='small'
							className="w-full text-slate-950"
						/>
					</div>
					{/* Model Name */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Model Alias <span className="text-red-500">*</span>
						</label>
						<Input
							placeholder="Enter model name"
							value={alias}
							onChange={(e) => setAlias(e.target.value)}
							disabled={isCreating}
							className="w-full"
							size='small'
						/>
					</div>

					{/* Description */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Model Description
						</label>
						<Textarea
							placeholder="Enter model description (optional)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							disabled={isCreating}
							rows={3}
							className="w-full"
							size='small'
						/>
					</div>

					{/* File Upload */}
					<div className="w-full [&_.ant-upload]:w-full [&_.ant-upload-wrapper]:w-full">
						<Upload {...uploadProps} className="w-full">
							<Button iconName='upload' disabled={isCreating} className="w-full" size='small' variant='secondary' label='Select Model File'>
								{uploadedFile ? uploadedFile.name : 'Select Model File'}
							</Button>
						</Upload>
					</div>
				</div>
			</Modal.Content>

			<Modal.Footer>
				<Button
					onClick={handleCreate}
					className='w-full'
					disabled={!uploadedFile || !alias.trim() || isCreating}
					variant="primary"
					size='small'
					label={isCreating ? 'Creating...' : 'Create Model'}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default CreateModelModal;
